import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import * as Yup from 'yup';
import {
  setBuDefaultFunctionalities,
  setBusFuncionalities,
  setBusFuncMap,
  setBusGroupExceptions,
  setBusProductExceptions,
  setBusStyles,
  setBusStylesMap,
  setIsLanguageSelected,
  setLanguage,
  setLoadingInfo,
  setStrings,
  setStringsDefault,
  setUser,
} from '../../store/actions';
import {patch} from '../../api/api';
import {uriUpdateUserdata} from '../../api/endpoints';
import PopupList from '../popup-list';
import getValuesToStoreWithBuDataFromGraphql from '../../graphQL';
import {getBusExceptions, getBusInfo} from '../../api/client';
import {ITenant} from '../../App';
import {functionalitiesAliasMap} from '../../util/strings';

interface Props {
  user: any;
  languageOptions: string[];
  stringsDefault: any;
  show: boolean;
  setShow(value: boolean): void;
  setLanguage(value: string): void;
  setIsLanguageSelected(value: boolean): void;
  reduxStrings: any;
  setStrings(value: any): void;
  setBuDefaultFunctionalities: Function;
  setUser: Function;
  selectedBu: any;
  setLoadingInfo(value: boolean): void;
  busMap: any;
  setGlobalStyles(value: any): void;
  setBusStylesMap(value: any): void;
  setBusFuncMap(value: any): void;
  setStringsDefault(value: any): void;
  setBusFuncionalities(value: any): void;
  setBusStyles(value: any): void;
  setBusProductExceptions(value: any): void;
  setBusGroupExceptions(value: any): void;
  tenant: ITenant;
}

const validationSchema = Yup.object().shape({});

const AppLanguageSelector = ({
  user,
  languageOptions,
  stringsDefault,
  show,
  setShow,
  setLanguage,
  setIsLanguageSelected,
  reduxStrings,
  setStrings,
  setBuDefaultFunctionalities,
  setUser,
  selectedBu,
  setLoadingInfo,
  busMap,
  tenant,
  setGlobalStyles,
  setBusStylesMap,
  setBusFuncMap,
  setStringsDefault,
  setBusFuncionalities,
  setBusStyles,
  setBusProductExceptions,
  setBusGroupExceptions,
}: Props) => {
  const [languagesList, setLanguagesList] = useState<any>();
  const [key, setKey] = useState(0);

  const featureName = 'LanguageSelector';

  const disableRequests = (busFuncMap: any) => {
    // setting "Pedidos" activa=false if 'Histórico de Reembolso' and 'Pré-autorizações' have activa=false
    Object.keys(busFuncMap).map((key: any) => {
      var reimbursements = busFuncMap[key].find(
        (func: any) =>
          func?.featureTemplateAlias === functionalitiesAliasMap.refund &&
          !func?.isVisible
      );
      var preauthorizations = busFuncMap[key].find(
        (func: any) =>
          func?.featureTemplateAlias ===
            functionalitiesAliasMap.authorization && !func?.isVisible
      );
      if (reimbursements && preauthorizations) {
        var pedidos = busFuncMap[key].find(
          (func: any) =>
            func.featureTemplateAlias === functionalitiesAliasMap.menuRequests
        );
        pedidos.isVisible = false;
      }
    });
  };

  const getBusFuncMap = (busMap: any, busFunc: any) => {
    let busFuncMap: any = {};
    if (busMap.length === 0) {
      busFuncMap['NOTCLIENT'] = busFunc[0].funcionalidade;
    } else {
      Object.keys(busMap).forEach((key: string, index: number) => {
        busFuncMap[key] = busFunc[index].funcionalidade;
      });
    }

    return busFuncMap;
  };

  const setBusFuncMapToStore = (busMap: any, busFunc: any) => {
    const busFuncMap = getBusFuncMap(busMap, busFunc);
    disableRequests(busFuncMap);

    setBusFuncMap(busFuncMap);
  };

  const setBusStyleMapToStore = (busMap: any, busStyles: any) => {
    let busStylesMap: any = {};
    if (busMap.length === 0) {
      busStylesMap[0] = busStyles[0];

      setGlobalStyles({
        ...busStylesMap[0],
        primaryColor: busStylesMap[0].primaryColor,
        secondaryColor: busStylesMap[0].secondaryColor,
        hoverColor: busStylesMap[0].hoverColor,
        url: busStylesMap[0].urlLogout,
      });
    } else {
      Object.keys(busMap).forEach(
        (key: string, index: number) => (busStylesMap[key] = busStyles[index])
      );
    }

    setBusStylesMap(busStylesMap);
  };

  const getLabels = async (language: string) => {
    let body: any;
    if (user.phone_number || user.phone_number !== '') {
      body = {
        userName: user.sub,
        fullName: `${user.given_name} ${user.family_name}`,
        nif: user.nif,
        phoneNumber: user.phone_number,
        email: user.email,
        language: language,
      };
    } else {
      body = {
        userName: user.sub,
        fullName: `${user.given_name} ${user.family_name}`,
        nif: user.nif,
        email: user.email,
        language: language,
      };
    }

    try {
      await patch(uriUpdateUserdata(), body, featureName).then(() => {
        setUser({...user, language: language});
      });
    } catch {
    } finally {
    }

    await getBusInfo(selectedBu.buId === 'NOTCLIENT', busMap).then((resp) => {
      setBusStyles(resp);
      setBusStyleMapToStore(busMap, resp);
    });

    await getValuesToStoreWithBuDataFromGraphql(
      user?.profile || user,
      selectedBu,
      busMap,
      tenant,
      stringsDefault?.pt?.messages?.default_language,
      language
    ).then(({busMap, busFunctionalities, stringsToStore}) => {
      setBusFuncMapToStore(busMap, busFunctionalities);

      setStrings(stringsToStore);
      setBusFuncionalities(busFunctionalities);
    });

    await getBusExceptions('group', busMap).then((resp) => {
      setBusGroupExceptions(resp);
    });

    await getBusExceptions('product', busMap).then((resp) => {
      setBusProductExceptions(resp);
    });

    setShow(false);
    setLoadingInfo(false);
  };

  const onSubmit = (value: string) => {
    setLoadingInfo(true);
    const languageValue = languageOptions?.find(
      (lang) => value === stringsDefault?.pt?.register?.[lang]
    );
    if (languageValue && languageValue !== user.language) {
      setLanguage(languageValue);
      setIsLanguageSelected(true);
      getLabels(languageValue);
    } else {
      setLoadingInfo(false);
    }
  };

  const generateLanguageList = () => {
    const result = languageOptions ? [...languageOptions] : [];

    const ptLangIndex = result.findIndex(
      (el: any) => el === stringsDefault?.pt?.messages?.default_language
    );

    if (ptLangIndex !== -1) {
      const ptLang = result.splice(ptLangIndex, 1);
      result.unshift(ptLang[0]);
    }

    setLanguagesList(
      result.map((option: any) => ({
        label: stringsDefault?.pt?.register?.[option],
        value: option,
      }))
    );
  };

  useEffect(() => {
    generateLanguageList();
  }, [stringsDefault, languageOptions]);

  useEffect(() => {
    setKey(key + 1);
  }, [user?.language, show]);

  return languagesList ? (
    <PopupList
      key={key}
      onSubmit={(value) => {
        onSubmit(value);
      }}
      onClose={() => {
        setShow(false);
      }}
      title={stringsDefault?.pt?.register?.language_selector}
      show={show}
      list={languagesList}
      setShowModal={setShow}
      titleOfButton={stringsDefault?.pt?.register?.contactActivation_buttonText}
      defaultValue={user.language}></PopupList>
  ) : (
    <></>
  );
};

const mapStateToProps = ({
  user,
  languageOptions,
  strings,
  stringsDefault,
  selectedBu,
  busMap,
  tenant,
}: any) => ({
  user: user.value,
  languageOptions: languageOptions.value,
  stringsDefault: stringsDefault.value,
  reduxStrings: strings.value,
  selectedBu: selectedBu.value,
  busMap: busMap.value,
  tenant: tenant.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setLanguage: (value: any) => dispatch(setLanguage(value)),
  setIsLanguageSelected: (value: any) => dispatch(setIsLanguageSelected(value)),
  setStrings: (value: any) => dispatch(setStrings(value)),
  setBuDefaultFunctionalities: (value: any) =>
    dispatch(setBuDefaultFunctionalities(value)),
  setUser: (value: any) => dispatch(setUser(value)),
  setLoadingInfo: (value: any) => dispatch(setLoadingInfo(value)),
  setGlobalStyles: (value: any) => dispatch(setLoadingInfo(value)),
  setBusStylesMap: (value: any) => dispatch(setBusStylesMap(value)),
  setBusFuncMap: (value: any) => dispatch(setBusFuncMap(value)),
  setStringsDefault: (value: any) => dispatch(setStringsDefault(value)),
  setBusFuncionalities: (value: any) => dispatch(setBusFuncionalities(value)),
  setBusStyles: (value: any) => dispatch(setBusStyles(value)),
  setBusProductExceptions: (value: any) =>
    dispatch(setBusProductExceptions(value)),
  setBusGroupExceptions: (value: any) => dispatch(setBusGroupExceptions(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(AppLanguageSelector)
);
